import { createContext, useContext, useState, ReactNode, useEffect, useRef, useMemo } from 'react';
import type { TradingAccount, Trade, CashFlow } from '../types';
import { accountService } from '../services/accountService';
import { accountStatsService } from '../services/accountStatsService';
import { tradeService } from '../services/tradeService';
import { useTradingAccount } from './TradingAccountContext';
import { useSimplifiedStats } from './SimplifiedStatsContext';
import { useAuth } from './AuthContext';
import { getAuth } from 'firebase/auth';
import { secureLog, sanitizeAccount } from '../utils/securityUtils';
import { notifyTradesUpdated, TRADES_UPDATED_EVENT } from '../utils/tradingAccountEvents';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  getActiveAccounts,
  mergeAccountWithSimplifiedEntry,
} from '../lib/simplifiedAccounts';

interface AccountContextType {
  selectedAccount: TradingAccount | null;
  accounts: TradingAccount[];
  activeAccounts: TradingAccount[];
  accountsLoading: boolean;
  setSelectedAccount: (account: TradingAccount | null, skipStatsUpdate?: boolean) => void;
  refreshSelectedAccount: (accountId?: string) => Promise<void>;
  updateAccountStats: (accountId: string) => Promise<void>;
  syncAccountAfterTradeImport: (accountId: string) => Promise<void>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

const SELECTED_ACCOUNT_KEY = 'selectedTradingAccount';

export function AccountProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { setCurrentAccount } = useTradingAccount();
  const {
    allAccounts,
    activeAccounts,
    loading: simplifiedLoading,
    getAccountEntry,
  } = useSimplifiedStats();

  const [selectedAccount, setSelectedAccountState] = useState<TradingAccount | null>(() => {
    const saved = localStorage.getItem(SELECTED_ACCOUNT_KEY);
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedAccountDoc, setSelectedAccountDoc] = useState<TradingAccount | null>(null);
  const [isUpdatingStats, setIsUpdatingStats] = useState(false);

  const selectedAccountRef = useRef<TradingAccount | null>(null);
  selectedAccountRef.current = selectedAccount;
  const statsRecalcAttemptedRef = useRef(new Set<string>());
  const lastValidatedAccountIdsRef = useRef('');

  const accounts = allAccounts;
  const accountsLoading = simplifiedLoading;

  const mergedSelectedAccount = useMemo(() => {
    const base = selectedAccountDoc ?? selectedAccount;
    if (!base?.id) {
      return null;
    }
    return mergeAccountWithSimplifiedEntry(base, getAccountEntry(base.id));
  }, [selectedAccountDoc, selectedAccount, getAccountEntry]);

  const setSelectedAccount = (account: TradingAccount | null, skipStatsUpdate = true) => {
    if (account) {
      const accountForStorage = { ...account };
      delete (accountForStorage as any).lastBrokerUpdate;
      localStorage.setItem(SELECTED_ACCOUNT_KEY, JSON.stringify(accountForStorage));

      if (!skipStatsUpdate) {
        setTimeout(() => {
          updateAccountStats(account.id);
        }, 0);
      }
    } else {
      localStorage.removeItem(SELECTED_ACCOUNT_KEY);
    }

    setSelectedAccountState(account);
    setCurrentAccount(account);
  };

  const validateStoredAccount = (availableAccounts: TradingAccount[]) => {
    const active = getActiveAccounts(availableAccounts);
    const accountIdsKey = active.map((acc) => acc.id).sort().join(',');
    const saved = localStorage.getItem(SELECTED_ACCOUNT_KEY);
    let storedAccountId: string | null = null;

    if (saved) {
      try {
        storedAccountId = JSON.parse(saved).id ?? null;
      } catch {
        storedAccountId = null;
      }
    }

    if (accountIdsKey === lastValidatedAccountIdsRef.current && storedAccountId) {
      const updatedSelected = active.find((acc) => acc.id === storedAccountId);
      if (updatedSelected) {
        setSelectedAccountState(updatedSelected);
        setCurrentAccount(updatedSelected);
      }
      return;
    }
    lastValidatedAccountIdsRef.current = accountIdsKey;

    secureLog.log('=== VALIDATION DU COMPTE STOCKÉ ===');
    secureLog.log('Comptes disponibles (non supprimés/désactivés):', active.map((acc) => sanitizeAccount(acc)));

    if (!saved) {
      if (active.length > 0) {
        setSelectedAccount(active[0], true);
      }
      return;
    }

    try {
      const storedAccount = JSON.parse(saved);
      const updatedAccount = active.find((acc) => acc.id === storedAccount.id);

      if (updatedAccount) {
        setSelectedAccount(updatedAccount, true);
      } else if (active.length > 0) {
        setSelectedAccount(active[0], true);
      } else {
        setSelectedAccount(null);
      }
    } catch (error) {
      secureLog.error('Erreur lors de la validation du compte stocké:', error);
    }
  };

  const updateAccountStats = async (accountId: string) => {
    if (!accountId || isUpdatingStats) return;

    try {
      setIsUpdatingStats(true);
      const auth = getAuth();
      if (!auth.currentUser) return;

      const existingStats = await accountStatsService.getAccountStats(accountId);
      if (existingStats && typeof existingStats.totalTrades === 'number') {
        return;
      }

      if (statsRecalcAttemptedRef.current.has(accountId)) {
        return;
      }
      statsRecalcAttemptedRef.current.add(accountId);

      const tradesRef = collection(db, 'trades');
      const q = query(
        tradesRef,
        where('userId', '==', auth.currentUser.uid),
        where('accountId', '==', accountId),
      );
      const tradesSnapshot = await getDocs(q);
      const trades = tradesSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as Trade));

      const cashFlowsRef = collection(db, 'cashFlows');
      const cashFlowsQuery = query(cashFlowsRef, where('accountId', '==', accountId));
      const cashFlowsSnapshot = await getDocs(cashFlowsQuery);
      const cashFlows = cashFlowsSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as CashFlow));

      const account = await accountService.getAccount(accountId);
      if (!account) return;

      await accountStatsService.updateAccountStats(
        accountId,
        trades,
        account.initialBalance || 0,
        (account as TradingAccount & { breakEvenThreshold?: number }).breakEvenThreshold,
        cashFlows,
      );
    } catch (error) {
      secureLog.error('Error updating account stats:', error);
    } finally {
      setIsUpdatingStats(false);
    }
  };

  const applyFreshAccountToState = (freshAccount: TradingAccount) => {
    setSelectedAccountDoc(freshAccount);

    if (selectedAccountRef.current?.id === freshAccount.id) {
      const merged = mergeAccountWithSimplifiedEntry(freshAccount, getAccountEntry(freshAccount.id));
      const accountForStorage = { ...merged };
      delete (accountForStorage as any).lastBrokerUpdate;
      localStorage.setItem(SELECTED_ACCOUNT_KEY, JSON.stringify(accountForStorage));
      setSelectedAccountState(merged);
      setCurrentAccount(merged);
    }
  };

  const syncAccountDataAfterTradesChange = async (accountId: string) => {
    if (!accountId) return;

    try {
      setIsUpdatingStats(true);
      const auth = getAuth();
      if (!auth.currentUser) return;

      const account = await accountService.getAccount(accountId);
      if (!account) return;

      const trades = await tradeService.getTradesByAccount(accountId);

      const cashFlowsRef = collection(db, 'cashFlows');
      const cashFlowsQuery = query(cashFlowsRef, where('accountId', '==', accountId));
      const cashFlowsSnapshot = await getDocs(cashFlowsQuery);
      const cashFlows = cashFlowsSnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as CashFlow));

      await accountStatsService.updateAccountStats(
        accountId,
        trades,
        account.initialBalance || 0,
        (account as TradingAccount & { breakEvenThreshold?: number }).breakEvenThreshold,
        cashFlows,
      );

      applyFreshAccountToState(account);
    } catch (error) {
      secureLog.error('Error syncing account after trades change:', error);
    } finally {
      setIsUpdatingStats(false);
    }
  };

  const syncAccountAfterTradeImport = async (accountId: string) => {
    if (!accountId) return;

    try {
      await syncAccountDataAfterTradesChange(accountId);
      statsRecalcAttemptedRef.current.add(accountId);
      notifyTradesUpdated(accountId);
    } catch (error) {
      secureLog.error('Error syncing account after trade import:', error);
    }
  };

  const refreshSelectedAccount = async (accountId?: string) => {
    const targetId = accountId ?? selectedAccountRef.current?.id;
    const active = getActiveAccounts(accounts);

    try {
      if (!targetId) {
        if (active.length > 0) {
          setSelectedAccount(active[0]);
        }
        return;
      }

      const freshAccount = await accountService.getAccount(targetId);

      if (freshAccount) {
        applyFreshAccountToState(freshAccount);
        return;
      }

      const fallbackAccount = active.find((acc) => acc.id === targetId);
      if (fallbackAccount) {
        setSelectedAccount(fallbackAccount, false);
      } else if (active.length > 0) {
        setSelectedAccount(active[0]);
      } else {
        setSelectedAccount(null);
      }
    } catch (error) {
      secureLog.error('Error refreshing account:', error);
    }
  };

  useEffect(() => {
    const handleTradesUpdated = (event: Event) => {
      const accountId = (event as CustomEvent<{ accountId?: string }>).detail?.accountId;
      if (!accountId) return;
      void syncAccountDataAfterTradesChange(accountId);
    };

    window.addEventListener(TRADES_UPDATED_EVENT, handleTradesUpdated);
    return () => window.removeEventListener(TRADES_UPDATED_EVENT, handleTradesUpdated);
  }, []);

  useEffect(() => {
    if (!user?.uid || simplifiedLoading) {
      return;
    }
    validateStoredAccount(accounts);
  }, [user?.uid, simplifiedLoading, accounts]);

  useEffect(() => {
    if (!user?.uid) {
      setSelectedAccount(null);
      setSelectedAccountDoc(null);
    }
  }, [user?.uid]);

  useEffect(() => {
    const selectedId = selectedAccount?.id;
    if (!selectedId || !user?.uid) {
      setSelectedAccountDoc(null);
      return;
    }

    const unsubscribe = accountService.subscribeToAccount(selectedId, (freshAccount) => {
      if (freshAccount) {
        applyFreshAccountToState(freshAccount);
      }
    });

    return () => unsubscribe();
  }, [selectedAccount?.id, user?.uid]);

  useEffect(() => {
    if (mergedSelectedAccount) {
      setCurrentAccount(mergedSelectedAccount);
    }
  }, [mergedSelectedAccount, setCurrentAccount]);

  useEffect(() => {
    const cleanStoredAccount = () => {
      const saved = localStorage.getItem(SELECTED_ACCOUNT_KEY);
      if (saved) {
        try {
          const storedAccount = JSON.parse(saved);
          if (storedAccount.lastBrokerUpdate) {
            const cleanAccount = { ...storedAccount };
            delete cleanAccount.lastBrokerUpdate;
            localStorage.setItem(SELECTED_ACCOUNT_KEY, JSON.stringify(cleanAccount));
          }
        } catch (error) {
          secureLog.error('Erreur lors du nettoyage du compte stocké:', error);
        }
      }
    };

    cleanStoredAccount();
  }, []);

  return (
    <AccountContext.Provider value={{
      selectedAccount: mergedSelectedAccount,
      accounts,
      activeAccounts,
      accountsLoading,
      setSelectedAccount,
      refreshSelectedAccount,
      updateAccountStats,
      syncAccountAfterTradeImport,
    }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}
